import { GameEvents, type GameSettingsChangedPayload } from "@/game/events/game-events";

export const GAME_SETTINGS_VERSION = 1 as const;

export type GameSettings = {
  version: typeof GAME_SETTINGS_VERSION;
  /** Player ATK at 0 attack stat. */
  playerBaseAttack: number;
  playerAttackPerStat: number;
  playerDefensePerStat: number;
  playerBaseDodge: number;
  playerDodgePerStat: number;
  playerDodgePerLevel: number;
  playerBaseHit: number;
  playerHitPerLevel: number;
  playerHitPerAttackStat: number;
  playerBaseCritChance: number;
  playerCritChancePerStat: number;
  playerBaseCritMultiplier: number;
  playerCritMultPerStat: number;
  /** Gap after normal attack at Lv.1 with 0 attack-speed AP (ms). */
  baseAttackGapMs: number;
  /** Ms removed from gap per level above 1 (stacks with AP, same budget as base→min). */
  attackGapReductionPerLevel: number;
  /** Attack-speed AP needed (linear) to reach minAttackGapMs from base alone. */
  attackSpeedStatForMinGap: number;
  minAttackGapMs: number;
  moveSpeedMultPerStat: number;
  /** HP restored per second at 0 hpRegen stat (combat + HUD). */
  playerBaseHpRegenPerSec: number;
  /** Extra HP per second per hpRegen stat point. */
  playerHpRegenPerStat: number;
  /** Extra max mana per mana stat point. */
  playerManaPerStat: number;
  playerAttackAnimFps: number;
  combatAttackRange: number;
  monsterDefaultLevel: number;
  monsterDefaultHp: number;
  monsterDefaultAttack: number;
  monsterDefaultDefense: number;
  monsterDefaultDodge: number;
  monsterDefaultXpReward: number;
  monsterDefaultCritRate: number;
  monsterDefaultCritMult: number;
  monsterHpPerLevelFactor: number;
  monsterStatScalePerLevel: number;
  combatDefenseK: number;
  combatDodgeScale: number;
  combatDodgeMin: number;
  combatDodgeMax: number;
  /** Defender home structure (solid) — base stats before upgrade cards. */
  homeStructureBaseHp: number;
  homeStructureBaseDef: number;
  homeStructureBaseRegenPerSec: number;
  homeStructureHpPerCard: number;
  homeStructureDefPerCard: number;
  homeStructureRegenPerCard: number;
  homeStructureHpCardMax: number;
  homeStructureDefCardMax: number;
  homeStructureRegenCardMax: number;
  homeStructureShieldHours: number;
  homeStructureRegenDelayAfterHitSec: number;
  homeStructurePotionHeal: number;
  homeStructurePotionCoinPrice: number;
  homeStructureHpCardCashCost: number;
  homeStructureDefCardCashCost: number;
  homeStructureRegenCardCashCost: number;
};

export const DEFAULT_GAME_SETTINGS: GameSettings = {
  version: GAME_SETTINGS_VERSION,
  playerBaseAttack: 10,
  playerAttackPerStat: 2,
  playerDefensePerStat: 2,
  playerBaseDodge: 20,
  playerDodgePerStat: 1.2,
  playerDodgePerLevel: 0.3,
  playerBaseHit: 50,
  playerHitPerLevel: 0.6,
  playerHitPerAttackStat: 0.4,
  playerBaseCritChance: 0.05,
  playerCritChancePerStat: 0.0045,
  playerBaseCritMultiplier: 1.5,
  playerCritMultPerStat: 0.01,
  baseAttackGapMs: 300,
  attackGapReductionPerLevel: 4,
  attackSpeedStatForMinGap: 100,
  minAttackGapMs: 10,
  moveSpeedMultPerStat: 0.01,
  playerBaseHpRegenPerSec: 1,
  playerHpRegenPerStat: 0.25,
  playerManaPerStat: 2,
  playerAttackAnimFps: 11,
  combatAttackRange: 2,
  monsterDefaultLevel: 1,
  monsterDefaultHp: 100,
  monsterDefaultAttack: 12,
  monsterDefaultDefense: 8,
  monsterDefaultDodge: 10,
  monsterDefaultXpReward: 50,
  monsterDefaultCritRate: 0.05,
  monsterDefaultCritMult: 1.4,
  monsterHpPerLevelFactor: 0.05,
  monsterStatScalePerLevel: 0.03,
  combatDefenseK: 120,
  combatDodgeScale: 0.85,
  combatDodgeMin: 0.05,
  combatDodgeMax: 0.75,
  homeStructureBaseHp: 100,
  homeStructureBaseDef: 10,
  homeStructureBaseRegenPerSec: 0.5,
  homeStructureHpPerCard: 80,
  homeStructureDefPerCard: 4,
  homeStructureRegenPerCard: 0.04,
  homeStructureHpCardMax: 150,
  homeStructureDefCardMax: 80,
  homeStructureRegenCardMax: 60,
  homeStructureShieldHours: 3,
  homeStructureRegenDelayAfterHitSec: 5,
  homeStructurePotionHeal: 100,
  homeStructurePotionCoinPrice: 10_000,
  homeStructureHpCardCashCost: 10,
  homeStructureDefCardCashCost: 10,
  homeStructureRegenCardCashCost: 10,
};

const STORAGE_KEY = "mind-survival/game-settings/v1";

let cache: GameSettings | null = null;

function clampNum(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

export function normalizeGameSettings(raw: unknown): GameSettings {
  const d = DEFAULT_GAME_SETTINGS;
  const p = raw && typeof raw === "object" ? (raw as Partial<GameSettings>) : {};
  return {
    version: GAME_SETTINGS_VERSION,
    playerBaseAttack: clampNum(p.playerBaseAttack, 1, 10_000, d.playerBaseAttack),
    playerAttackPerStat: clampNum(p.playerAttackPerStat, 0, 500, d.playerAttackPerStat),
    playerDefensePerStat: clampNum(p.playerDefensePerStat, 0, 500, d.playerDefensePerStat),
    playerBaseDodge: clampNum(p.playerBaseDodge, 0, 10_000, d.playerBaseDodge),
    playerDodgePerStat: clampNum(p.playerDodgePerStat, 0, 100, d.playerDodgePerStat),
    playerDodgePerLevel: clampNum(p.playerDodgePerLevel, 0, 50, d.playerDodgePerLevel),
    playerBaseHit: clampNum(p.playerBaseHit, 0, 10_000, d.playerBaseHit),
    playerHitPerLevel: clampNum(p.playerHitPerLevel, 0, 50, d.playerHitPerLevel),
    playerHitPerAttackStat: clampNum(p.playerHitPerAttackStat, 0, 50, d.playerHitPerAttackStat),
    playerBaseCritChance: clampNum(p.playerBaseCritChance, 0, 1, d.playerBaseCritChance),
    playerCritChancePerStat: clampNum(p.playerCritChancePerStat, 0, 0.5, d.playerCritChancePerStat),
    playerBaseCritMultiplier: clampNum(p.playerBaseCritMultiplier, 1, 10, d.playerBaseCritMultiplier),
    playerCritMultPerStat: clampNum(p.playerCritMultPerStat, 0, 1, d.playerCritMultPerStat),
    baseAttackGapMs: clampNum(p.baseAttackGapMs, 0, 5000, d.baseAttackGapMs),
    attackGapReductionPerLevel: clampNum(
      p.attackGapReductionPerLevel,
      0,
      500,
      d.attackGapReductionPerLevel,
    ),
    attackSpeedStatForMinGap: clampNum(p.attackSpeedStatForMinGap, 1, 10_000, d.attackSpeedStatForMinGap),
    minAttackGapMs: clampNum(p.minAttackGapMs, 0, 2000, d.minAttackGapMs),
    moveSpeedMultPerStat: clampNum(p.moveSpeedMultPerStat, 0, 1, d.moveSpeedMultPerStat),
    playerBaseHpRegenPerSec: clampNum(
      p.playerBaseHpRegenPerSec,
      0,
      10_000,
      d.playerBaseHpRegenPerSec,
    ),
    playerHpRegenPerStat: clampNum(p.playerHpRegenPerStat, 0, 500, d.playerHpRegenPerStat),
    playerManaPerStat: clampNum(p.playerManaPerStat, 0, 500, d.playerManaPerStat),
    playerAttackAnimFps: clampNum(p.playerAttackAnimFps, 1, 60, d.playerAttackAnimFps),
    combatAttackRange: clampNum(p.combatAttackRange, 1, 20, d.combatAttackRange),
    monsterDefaultLevel: clampNum(p.monsterDefaultLevel, 1, 100, d.monsterDefaultLevel),
    monsterDefaultHp: clampNum(p.monsterDefaultHp, 1, 1_000_000, d.monsterDefaultHp),
    monsterDefaultAttack: clampNum(p.monsterDefaultAttack, 1, 100_000, d.monsterDefaultAttack),
    monsterDefaultDefense: clampNum(p.monsterDefaultDefense, 0, 100_000, d.monsterDefaultDefense),
    monsterDefaultDodge: clampNum(p.monsterDefaultDodge, 0, 100_000, d.monsterDefaultDodge),
    monsterDefaultXpReward: clampNum(p.monsterDefaultXpReward, 0, 10_000_000, d.monsterDefaultXpReward),
    monsterDefaultCritRate: clampNum(p.monsterDefaultCritRate, 0, 1, d.monsterDefaultCritRate),
    monsterDefaultCritMult: clampNum(p.monsterDefaultCritMult, 1, 10, d.monsterDefaultCritMult),
    monsterHpPerLevelFactor: clampNum(p.monsterHpPerLevelFactor, 0, 2, d.monsterHpPerLevelFactor),
    monsterStatScalePerLevel: clampNum(p.monsterStatScalePerLevel, 0, 1, d.monsterStatScalePerLevel),
    combatDefenseK: clampNum(p.combatDefenseK, 1, 10_000, d.combatDefenseK),
    combatDodgeScale: clampNum(p.combatDodgeScale, 0, 5, d.combatDodgeScale),
    combatDodgeMin: clampNum(p.combatDodgeMin, 0, 1, d.combatDodgeMin),
    combatDodgeMax: clampNum(p.combatDodgeMax, 0, 1, d.combatDodgeMax),
    homeStructureBaseHp: clampNum(p.homeStructureBaseHp, 1, 10_000_000, d.homeStructureBaseHp),
    homeStructureBaseDef: clampNum(p.homeStructureBaseDef, 0, 100_000, d.homeStructureBaseDef),
    homeStructureBaseRegenPerSec: clampNum(
      p.homeStructureBaseRegenPerSec,
      0,
      10_000,
      d.homeStructureBaseRegenPerSec,
    ),
    homeStructureHpPerCard: clampNum(p.homeStructureHpPerCard, 1, 1_000_000, d.homeStructureHpPerCard),
    homeStructureDefPerCard: clampNum(p.homeStructureDefPerCard, 0, 10_000, d.homeStructureDefPerCard),
    homeStructureRegenPerCard: clampNum(
      p.homeStructureRegenPerCard,
      0,
      1000,
      d.homeStructureRegenPerCard,
    ),
    homeStructureHpCardMax: clampNum(p.homeStructureHpCardMax, 0, 10_000, d.homeStructureHpCardMax),
    homeStructureDefCardMax: clampNum(p.homeStructureDefCardMax, 0, 10_000, d.homeStructureDefCardMax),
    homeStructureRegenCardMax: clampNum(
      p.homeStructureRegenCardMax,
      0,
      10_000,
      d.homeStructureRegenCardMax,
    ),
    homeStructureShieldHours: clampNum(p.homeStructureShieldHours, 0, 168, d.homeStructureShieldHours),
    homeStructureRegenDelayAfterHitSec: clampNum(
      p.homeStructureRegenDelayAfterHitSec,
      0,
      600,
      d.homeStructureRegenDelayAfterHitSec,
    ),
    homeStructurePotionHeal: clampNum(p.homeStructurePotionHeal, 1, 1_000_000, d.homeStructurePotionHeal),
    homeStructurePotionCoinPrice: clampNum(
      p.homeStructurePotionCoinPrice,
      0,
      1_000_000_000,
      d.homeStructurePotionCoinPrice,
    ),
    homeStructureHpCardCashCost: clampNum(
      p.homeStructureHpCardCashCost,
      0,
      1_000_000,
      d.homeStructureHpCardCashCost,
    ),
    homeStructureDefCardCashCost: clampNum(
      p.homeStructureDefCardCashCost,
      0,
      1_000_000,
      d.homeStructureDefCardCashCost,
    ),
    homeStructureRegenCardCashCost: clampNum(
      p.homeStructureRegenCardCashCost,
      0,
      1_000_000,
      d.homeStructureRegenCardCashCost,
    ),
  };
}

function loadFromStorage(): GameSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_GAME_SETTINGS };
    return normalizeGameSettings(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_GAME_SETTINGS };
  }
}

function dispatchChanged(settings: GameSettings): void {
  const detail: GameSettingsChangedPayload = { settings };
  window.dispatchEvent(new CustomEvent(GameEvents.GameSettingsChanged, { detail }));
}

export function getGameSettings(): GameSettings {
  if (!cache) cache = loadFromStorage();
  return cache;
}

export function saveGameSettings(patch: Partial<GameSettings>): GameSettings {
  const next = normalizeGameSettings({ ...getGameSettings(), ...patch });
  cache = next;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  dispatchChanged(next);
  return next;
}

export function resetGameSettings(): GameSettings {
  cache = { ...DEFAULT_GAME_SETTINGS };
  localStorage.removeItem(STORAGE_KEY);
  dispatchChanged(cache);
  return cache;
}

export type GameSettingsFieldMeta = {
  key: keyof GameSettings;
  label: string;
  /** Plain-language effect when GM raises this value above default. */
  effectIfHigher: string;
  group: "player" | "monster" | "combat" | "homeStructure";
  step?: number;
  min?: number;
  max?: number;
};

const HOME_STRUCTURE_GM_FIELDS: GameSettingsFieldMeta[] = [
  {
    key: "homeStructureBaseHp",
    label: "HP บ้านฐาน",
    effectIfHigher: "บ้านทนต่อการโจมตีมากขึ้นก่อนอัพการ์ด",
    group: "homeStructure",
    min: 1,
  },
  {
    key: "homeStructureBaseDef",
    label: "ป้องกันบ้านฐาน",
    effectIfHigher: "ลดดาเมจที่เข้าบ้าน",
    group: "homeStructure",
    min: 0,
  },
  {
    key: "homeStructureBaseRegenPerSec",
    label: "ฟื้นบ้าน HP/วิ (ฐาน)",
    effectIfHigher: "ฟื้นหลังเลิกโดนตีเร็วขึ้น",
    group: "homeStructure",
    min: 0,
    step: 0.01,
  },
  {
    key: "homeStructureHpPerCard",
    label: "HP ต่อบัตรอัพ",
    effectIfHigher: "บัตร HP บ้านคุ้มขึ้น",
    group: "homeStructure",
    min: 1,
  },
  {
    key: "homeStructureDefPerCard",
    label: "ป้องกันต่อบัตรอัพ",
    effectIfHigher: "บัตรป้องกันบ้านคุ้มขึ้น",
    group: "homeStructure",
    min: 0,
  },
  {
    key: "homeStructureRegenPerCard",
    label: "ฟื้นต่อบัตรอัพ (HP/วิ)",
    effectIfHigher: "บัตรฟื้นบ้านคุ้มขึ้น",
    group: "homeStructure",
    min: 0,
    step: 0.01,
  },
  {
    key: "homeStructureHpCardMax",
    label: "เพดานบัตร HP",
    effectIfHigher: "อัพ HP บ้านได้ลึกขึ้น",
    group: "homeStructure",
    min: 0,
  },
  {
    key: "homeStructureDefCardMax",
    label: "เพดานบัตรป้องกัน",
    effectIfHigher: "อัพป้องกันบ้านได้ลึกขึ้น",
    group: "homeStructure",
    min: 0,
  },
  {
    key: "homeStructureRegenCardMax",
    label: "เพดานบัตรฟื้น",
    effectIfHigher: "อัพฟื้นบ้านได้ลึกขึ้น",
    group: "homeStructure",
    min: 0,
  },
  {
    key: "homeStructureShieldHours",
    label: "เกราะหลังบ้านแตก (ชม.)",
    effectIfHigher: "บ้านไม่รับโจมตีนานขึ้นหลังแตก",
    group: "homeStructure",
    min: 0,
    step: 0.25,
  },
  {
    key: "homeStructureRegenDelayAfterHitSec",
    label: "หน่วงก่อนฟื้นหลังโดนตี (วิ)",
    effectIfHigher: "ต้องรอนานก่อนฟื้น HP บ้าน",
    group: "homeStructure",
    min: 0,
  },
  {
    key: "homeStructurePotionHeal",
    label: "ยาบ้านฟื้น HP",
    effectIfHigher: "ยาบ้านหนึ่งขวดฟื้นมากขึ้น",
    group: "homeStructure",
    min: 1,
  },
  {
    key: "homeStructurePotionCoinPrice",
    label: "ราคายาบ้าน (ทอง)",
    effectIfHigher: "ซื้อยาบ้านแพงขึ้น",
    group: "homeStructure",
    min: 0,
  },
  {
    key: "homeStructureHpCardCashCost",
    label: "ราคาบัตร HP บ้าน (Cash)",
    effectIfHigher: "บัตร HP บ้านแพงขึ้น",
    group: "homeStructure",
    min: 0,
  },
  {
    key: "homeStructureDefCardCashCost",
    label: "ราคาบัตรป้องกันบ้าน (Cash)",
    effectIfHigher: "บัตรป้องกันแพงขึ้น",
    group: "homeStructure",
    min: 0,
  },
  {
    key: "homeStructureRegenCardCashCost",
    label: "ราคาบัตรฟื้นบ้าน (Cash)",
    effectIfHigher: "บัตรฟื้นแพงขึ้น",
    group: "homeStructure",
    min: 0,
  },
];

export const GAME_SETTINGS_FIELD_META: GameSettingsFieldMeta[] = [
  {
    key: "playerBaseAttack",
    label: "โจมตีฐาน (0 สเตต)",
    effectIfHigher: "ดาเมจตีธรรมดา/สกิลสูงขึ้นทุกคน แม้ยังไม่ลงสเตต ATK",
    group: "player",
    min: 1,
  },
  {
    key: "playerAttackPerStat",
    label: "โจมตีต่อจุดสเตต ATK",
    effectIfHigher: "ลง ATK แต่ละจุดเพิ่มดาเมจมากขึ้น — สเตต + เยอะโตเร็ว",
    group: "player",
    min: 0,
  },
  {
    key: "playerDefensePerStat",
    label: "ป้องกันต่อจุดสเตต DEF",
    effectIfHigher: "ลง DEF แล้วโดนตีเจ็บน้อยลงชัดเจน",
    group: "player",
    min: 0,
  },
  {
    key: "playerBaseDodge",
    label: "หลบฐาน",
    effectIfHigher: "หลบการโจมตีได้บ่อยขึ้นแม้ยังไม่ลงสเตตหลบ",
    group: "player",
    min: 0,
  },
  {
    key: "playerDodgePerStat",
    label: "หลบต่อจุดสเตต",
    effectIfHigher: "ลงสเตตหลบแล้วโอกาสหลบพุ่งขึ้นเร็ว",
    group: "player",
    min: 0,
    step: 0.1,
  },
  {
    key: "playerDodgePerLevel",
    label: "หลบต่อเลเวล",
    effectIfHigher: "เลเวลสูงหลบดีขึ้นโดยไม่ต้องลงแต้ม",
    group: "player",
    min: 0,
    step: 0.1,
  },
  {
    key: "playerBaseHit",
    label: "แม่นยำฐาน (hit)",
    effectIfHigher: "ตีโดนบ่อยขึ้น ศัตรูหลบได้ยากลง",
    group: "player",
    min: 0,
  },
  {
    key: "playerHitPerLevel",
    label: "hit ต่อเลเวล",
    effectIfHigher: "เลเวลสูงแม่นขึ้นเรื่อย ๆ",
    group: "player",
    min: 0,
    step: 0.1,
  },
  {
    key: "playerHitPerAttackStat",
    label: "hit ต่อจุด ATK",
    effectIfHigher: "ลง ATK แล้วได้ทั้งดาเมจและความแม่นยำมากขึ้น",
    group: "player",
    min: 0,
    step: 0.1,
  },
  {
    key: "playerBaseCritChance",
    label: "อัตราคริฐาน",
    effectIfHigher: "คริติดบ่อยขึ้น (0–1 = 0–100%)",
    group: "player",
    min: 0,
    max: 1,
    step: 0.001,
  },
  {
    key: "playerCritChancePerStat",
    label: "คริต่อจุดสเตต",
    effectIfHigher: "ลงสเตตคริแล้วอัตราคริสูงขึ้นเร็ว",
    group: "player",
    min: 0,
    step: 0.0001,
  },
  {
    key: "playerBaseCritMultiplier",
    label: "ตัวคูณคริฐาน",
    effectIfHigher: "คริแรงขึ้น (×1.5 = +50% ดาเมจตอนคริ)",
    group: "player",
    min: 1,
    step: 0.01,
  },
  {
    key: "playerCritMultPerStat",
    label: "ตัวคูณคริต่อจุด",
    effectIfHigher: "ลงสเตตคริดาเมจคริทวีขึ้นชัด",
    group: "player",
    min: 0,
    step: 0.01,
  },
  {
    key: "baseAttackGapMs",
    label: "หน่วงฐาน Lv.1 / 0 AP (ms)",
    effectIfHigher: "Lv.1 ยังไม่ลงความเร็วโจมตี → ตีช้าขึ้น",
    group: "player",
    min: 0,
  },
  {
    key: "attackGapReductionPerLevel",
    label: "ลดหน่วงต่อเลเวล (ms)",
    effectIfHigher: "เลเวลสูงตีถี่ขึ้นแม้ยังไม่ลง AP (รวมงบเดียวกับ AP ถึงหน่วงต่ำสุด)",
    group: "player",
    min: 0,
  },
  {
    key: "attackSpeedStatForMinGap",
    label: "AP ความเร็วโจมตีถึงหน่วงต่ำสุด",
    effectIfHigher: "ต้องลง AP มากขึ้นถึงจะถึงหน่วงต่ำสุด (เช่น 100 = ลง 100 จุดถึง min ms)",
    group: "player",
    min: 1,
  },
  {
    key: "minAttackGapMs",
    label: "หน่วงต่ำสุด (ms)",
    effectIfHigher: "เพดานเร็วสุด — สูงขึ้น = ตีเร็วสุดไม่ถี่เท่าเดิม (ค่าเริ่ม 10ms ที่ 100 AP)",
    group: "player",
    min: 0,
  },
  {
    key: "moveSpeedMultPerStat",
    label: "คูณความเร็วเดินต่อจุด",
    effectIfHigher: "ลงสเตตเดินแล้ววิ่งเร็วขึ้นมาก",
    group: "player",
    min: 0,
    step: 0.01,
  },
  {
    key: "playerBaseHpRegenPerSec",
    label: "ฟื้น HP ฐาน (ต่อวินาที)",
    effectIfHigher: "ทุกคนฟื้นเลือดเร็วขึ้นแม้ยังไม่ลงสเตตฟื้นเลือด",
    group: "player",
    min: 0,
    step: 0.1,
  },
  {
    key: "playerHpRegenPerStat",
    label: "ฟื้น HP ต่อจุดสเตตฟื้นเลือด",
    effectIfHigher: "ลงสเตตฟื้นเลือดแล้วได้ HP/วิ มากขึ้น",
    group: "player",
    min: 0,
    step: 0.05,
  },
  {
    key: "playerManaPerStat",
    label: "มานาสูงสุดต่อจุดสเตตมานา",
    effectIfHigher: "ลงสเตตมานาแล้วแม็กซ์มานาเพิ่ม",
    group: "player",
    min: 0,
    step: 0.5,
  },
  {
    key: "playerAttackAnimFps",
    label: "FPS แอนิโจมตี",
    effectIfHigher: "แอนิตีเร็วขึ้น (ยังมีหน่วง ms หลังตีแยก)",
    group: "player",
    min: 1,
    max: 60,
  },
  {
    key: "combatAttackRange",
    label: "ระยะตี (ช่องกริด)",
    effectIfHigher: "เริ่มตี/สู้จากไกลขึ้น",
    group: "combat",
    min: 1,
    max: 20,
  },
  {
    key: "monsterDefaultLevel",
    label: "เลเวลมอนสเตอร์ใหม่",
    effectIfHigher: "ชิ้นมอนสเตอร์ใหม่แรงและทนกว่า (สูตรสเกลเลเวล)",
    group: "monster",
    min: 1,
    max: 100,
  },
  {
    key: "monsterDefaultHp",
    label: "HP ฐาน",
    effectIfHigher: "มอนสเตอร์ใหม่เลือดเยอะ ฆ่านานขึ้น",
    group: "monster",
    min: 1,
  },
  {
    key: "monsterDefaultAttack",
    label: "ATK ฐาน",
    effectIfHigher: "มอนสเตอร์ตีผู้เล่นแรงขึ้น",
    group: "monster",
    min: 1,
  },
  {
    key: "monsterDefaultDefense",
    label: "DEF ฐาน",
    effectIfHigher: "ผู้เล่นทำดาเมจมอนสเตอร์ได้น้อยลง",
    group: "monster",
    min: 0,
  },
  {
    key: "monsterDefaultDodge",
    label: "หลบฐาน",
    effectIfHigher: "มอนสเตอร์หลบผู้เล่นบ่อยขึ้น",
    group: "monster",
    min: 0,
  },
  {
    key: "monsterDefaultXpReward",
    label: "EXP รางวัล",
    effectIfHigher: "ฆ่าแล้วได้ EXP มากขึ้น",
    group: "monster",
    min: 0,
  },
  {
    key: "monsterDefaultCritRate",
    label: "อัตราคริฐาน",
    effectIfHigher: "มอนสเตอร์คริผู้เล่นบ่อยขึ้น",
    group: "monster",
    min: 0,
    max: 1,
    step: 0.001,
  },
  {
    key: "monsterDefaultCritMult",
    label: "ตัวคูณคริฐาน",
    effectIfHigher: "คริของมอนสเตอร์แรงขึ้น",
    group: "monster",
    min: 1,
    step: 0.01,
  },
  {
    key: "monsterHpPerLevelFactor",
    label: "สเกล HP ต่อเลเวล",
    effectIfHigher: "เลเวลมอนสเตอร์สูง → HP โตเร็วขึ้น",
    group: "monster",
    min: 0,
    step: 0.01,
  },
  {
    key: "monsterStatScalePerLevel",
    label: "สเกล ATK ต่อเลเวล",
    effectIfHigher: "เลเวลมอนสเตอร์สูง → ATK โตเร็วขึ้น",
    group: "monster",
    min: 0,
    step: 0.01,
  },
  {
    key: "combatDefenseK",
    label: "ค่า K ลดดาเมจ (DEF)",
    effectIfHigher: "K สูง → DEF ลดดาเมจได้น้อยลง ดาเมจทะลุมากขึ้น",
    group: "combat",
    min: 1,
  },
  {
    key: "combatDodgeScale",
    label: "สเกลหลบ",
    effectIfHigher: "ค่าหลบมีผลแรงขึ้น หลบง่ายขึ้นทั้งสองฝ่าย",
    group: "combat",
    min: 0,
    step: 0.01,
  },
  {
    key: "combatDodgeMin",
    label: "หลบต่ำสุด",
    effectIfHigher: "แม้หลบต่ำก็ยังมีโอกาสหลบขั้นต่ำสูงขึ้น",
    group: "combat",
    min: 0,
    max: 1,
    step: 0.01,
  },
  {
    key: "combatDodgeMax",
    label: "หลบสูงสุด",
    effectIfHigher: "เพดานหลบสูง — สเตตหลบสูงอาจหลบเกือบตลอด",
    group: "combat",
    min: 0,
    max: 1,
    step: 0.01,
  },
  ...HOME_STRUCTURE_GM_FIELDS,
];

export const GAME_SETTINGS_GROUP_LABEL: Record<GameSettingsFieldMeta["group"], string> = {
  player: "ผู้เล่น & ตีธรรมดา",
  monster: "มอนสเตอร์ (ค่าเริ่มชิ้นใหม่)",
  combat: "สูตรต่อสู้",
  homeStructure: "บ้านป้องกัน (โครงสร้าง)",
};
